import type { RefObject, ReactNode, MutableRefObject } from 'react';
import React, { Suspense, memo, useMemo, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { checkSSRPartialSuccess } from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess';

import type {
	LoaderResultType,
	LoadedModuleType,
	SSRSuccessCheckName,
	ActivationTrigger,
} from '../types';
import { ActivationReason, ActivationTriggerType } from '../types';

import { Gate, type GateType, type GateComponentProps } from './Gate';

type BoundaryProps<P> = GateComponentProps<P> & {
	resolved: boolean;
	boundaryName: string;
	triggers?: ActivationTrigger[];
	immediateActivationCondition?: boolean;
};

export function SnapshotFallback<P>({
	id,
	gate,
	loader,
	hostRef,
	onLoaded,
	loaderPromiseRef,
	boundaryName,
	activateComponent,
	ssrSuccessCheckName,
	ssrSuccess,
	triggers,
}: {
	id: string;
	gate: GateType | null;
	loader?: () => LoaderResultType<P>;
	hostRef?: RefObject<HTMLDivElement | null>;
	onLoaded?: (m: LoadedModuleType<P>) => void;
	loaderPromiseRef?: MutableRefObject<Promise<void> | null>;
	boundaryName: string;
	activateComponent: (reason: ActivationReason) => void;
	ssrSuccessCheckName?: SSRSuccessCheckName;
	ssrSuccess?: boolean;
	triggers?: ActivationTrigger[];
}): React.JSX.Element | null {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const content = useMemo(() => {
		if (typeof window === 'undefined') {
			return null;
		}
		const el = document.querySelector(`[data-loadable-boundary-content="${id}"]`);
		const innerHTML = el?.innerHTML || '';
		return innerHTML || null;
	}, [id]);

	const isSsrOnly = useMemo(
		() => triggers?.some((trigger) => trigger.type === ActivationTriggerType.SSR_ONLY) ?? false,
		[triggers],
	);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'loadable',
				action: 'fallbackRendered',
				actionSubject: 'loadableComponent',
				attributes: {
					boundaryName,
					hadSSRContent: !!content,
					placeholderId: id,
					isSsrOnly,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, boundaryName, content, id, isSsrOnly]);

	useEffect(() => {
		// Auto-activate if we have no/incomplete SSR content to show
		// If we have SSR content, we should wait for other means of activation (hover, timeout, etc.)
		const ssrCheckFailed = ssrSuccessCheckName
			? !checkSSRPartialSuccess(ssrSuccessCheckName)
			: !ssrSuccess;

		if ((!content || ssrCheckFailed) && gate && gate.getStatus() !== 'resolved') {
			activateComponent(ActivationReason.FALLBACK);
		}
	}, [
		content,
		gate,
		id,
		loader,
		hostRef,
		onLoaded,
		loaderPromiseRef,
		boundaryName,
		createAnalyticsEvent,
		activateComponent,
		ssrSuccessCheckName,
		ssrSuccess,
	]);

	if (!content) {
		return null;
	}

	// Render  SSR content directly for fast fallback display
	// eslint-disable-next-line react/no-danger
	return <div data-loadable-boundary-content={id} dangerouslySetInnerHTML={{ __html: content }} />;
}

export const Boundary = memo(
	function BoundaryComponent<P>({
		gate,
		id,
		loader,
		hostRef,
		onLoaded,
		render,
		loaderPromiseRef,
		boundaryName,
		ssrSuccessCheckName,
		ssrSuccess,
		activateComponent,
		triggers,
		immediateActivationCondition,
	}: BoundaryProps<P>): ReactNode {
		// We add a wrapper div above Gate to match the structure of the SnapshotFallback
		// that way, if React forces the fallback to render it will not cause a TTVC mutation as long as the SSR result is correct
		return (
			<Suspense
				fallback={
					<SnapshotFallback
						id={id}
						gate={gate}
						loader={loader}
						hostRef={hostRef}
						onLoaded={onLoaded}
						loaderPromiseRef={loaderPromiseRef}
						boundaryName={boundaryName}
						activateComponent={activateComponent}
						ssrSuccessCheckName={ssrSuccessCheckName}
						ssrSuccess={ssrSuccess}
						triggers={triggers}
					/>
				}
			>
				<div data-loadable-boundary-content={id}>
					<Gate
						gate={gate}
						id={id}
						loader={loader}
						hostRef={hostRef}
						onLoaded={onLoaded}
						render={render}
						loaderPromiseRef={loaderPromiseRef}
						ssrSuccessCheckName={ssrSuccessCheckName}
						ssrSuccess={ssrSuccess}
						activateComponent={activateComponent}
						immediateActivationCondition={immediateActivationCondition}
					/>
				</div>
			</Suspense>
		);
	},
	(prev: BoundaryProps<unknown>, next: BoundaryProps<unknown>) => {
		// Once the gate is resolved, allow all prop changes to flow through
		if (next.resolved) {
			return false;
		}
		// Before resolution, only re-render when resolved state changes and when immediateActivation changes
		return (
			prev.resolved === next.resolved &&
			prev.immediateActivationCondition === next.immediateActivationCondition
		);
	},
) as <P>(props: BoundaryProps<P>) => ReactNode;
